import React, { useMemo } from 'react';
import { PolygonLayer, ScatterplotLayer, TextLayer, IconLayer } from '@deck.gl/layers';
import isEqual from 'lodash.isequal';

// Helper for stable memoization with deep equality
function useStableMemo(factory, deps) {
  const ref = React.useRef();
  const value = factory();
  if (!ref.current || !isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

// Memoized color conversion function
const hexToRgba = (hex, alpha) => {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b, alpha];
};

// Helper function to calculate polygon center for label positioning
const calculatePolygonCenter = (polygon) => {
  if (!polygon || polygon.length === 0) return null;
  
  let sumLat = 0;
  let sumLng = 0;
  
  for (const [lng, lat] of polygon) {
    sumLat += lat;
    sumLng += lng;
  }
  
  return {
    lat: sumLat / polygon.length,
    lng: sumLng / polygon.length
  };
};

// Create pill-shaped SVG icon for team member labels
const createPillIcon = (width = 120, height = 32) => {
  const radius = height / 2;
  const path = `M ${radius} 0 L ${width - radius} 0 Q ${width} 0 ${width} ${radius} L ${width} ${height - radius} Q ${width} ${height} ${width - radius} ${height} L ${radius} ${height} Q 0 ${height} 0 ${height - radius} L 0 ${radius} Q 0 0 ${radius} 0 Z`;
  
  return `data:image/svg+xml;utf8,<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"><path d="${path}" fill="black" stroke="white" stroke-width="2"/></svg>`;
};

const TeamAssignmentLayers = ({ teamData, zoomLevel }) => {
  // Debug: Log the zoom level and team data
  

  // Prepare area polygons for PolygonLayer - optimized with stable reference
  const areaPolygons = useStableMemo(() => {
    if (!teamData?.areas || !teamData?.members) return [];
    return Object.entries(teamData.areas).map(([areaName, coords]) => {
      const member = teamData.members.find(m => m.name === areaName);
      return {
        areaName,
        color: member?.color || '#ff0000',
        polygon: coords.map(c => [c.lng, c.lat])
      };
    });
  }, [teamData]);

  // Prepare label data for polygons - optimized with stable reference
  const polygonLabels = useStableMemo(() => {
    if (!teamData?.areas || !teamData?.members) return [];
    return Object.entries(teamData.areas).map(([areaName, coords]) => {
      const member = teamData.members.find(m => m.name === areaName);
      const center = calculatePolygonCenter(coords.map(c => [c.lng, c.lat]));
      return {
        position: [center.lng, center.lat],
        text: areaName,
        color: member?.color || '#ff0000'
      };
    }).filter(label => label.position[0] !== null && label.position[1] !== null);
  }, [teamData]);

  // Debug: Log the label data
  

  // Memoized polygon layer with minimal updateTriggers
  const polygonLayer = useMemo(() => {
    if (areaPolygons.length === 0) return null;
    return new PolygonLayer({
      id: 'area-polygons',
      data: areaPolygons,
      getPolygon: d => [d.polygon],
      getFillColor: d => {
        const alpha = zoomLevel >= 15 ? 0 : 80;
        return hexToRgba(d.color, alpha);
      },
      getLineColor: d => [0, 0, 0, 200],
      lineWidthMinPixels: 2,
      pickable: false,
      filled: true,
      stroked: true,
      opacity: 0.3,
      updateTriggers: {
        getFillColor: [zoomLevel]
      }
    });
  }, [areaPolygons, zoomLevel]);

  // Memoized polygon label background layer (pill-shaped black background)
  const polygonLabelBgLayer = useMemo(() => {
    if (polygonLabels.length === 0 || zoomLevel < 10 || zoomLevel >= 15) {
      
      return null;
    }
    
    
    
    return new IconLayer({
      id: 'polygon-labels-bg',
      data: polygonLabels,
      getPosition: d => d.position,
      iconAtlas: createPillIcon(120, 32),
      iconMapping: {
        pill: { x: 0, y: 0, width: 120, height: 32, mask: false }
      },
      getIcon: () => 'pill',
      sizeScale: 1,
      getSize: 32,
      pickable: false
    });
  }, [polygonLabels, zoomLevel]);

  // Memoized polygon labels layer with white text, no outline
  const polygonLabelsLayer = useMemo(() => {
    if (polygonLabels.length === 0 || zoomLevel < 10 || zoomLevel >= 15) {
      
      return null;
    }
    
    
    
    return new TextLayer({
      id: 'polygon-labels',
      data: polygonLabels,
      getPosition: d => d.position,
      getText: d => d.text,
      getSize: 18, // Slightly larger text
      getColor: [255,255,255,255], // White text
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      fontWeight: '600', // Semi-bold for better readability
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 18,
      letterSpacing: 0.5, // Tighter letter spacing
      sizeScale: 1.0, // No scaling
      pickable: false,
      fontSettings: { sdf: true },
      updateTriggers: {
        getColor: [zoomLevel]
      }
    });
  }, [polygonLabels, zoomLevel]);

  // Memoized polygon center marker layer (location-style dot)
  const polygonCenterDotLayer = useMemo(() => {
    if (polygonLabels.length === 0 || zoomLevel >= 10) return null;
    
    return new ScatterplotLayer({
      id: 'polygon-center-dots',
      data: polygonLabels,
      getPosition: d => d.position,
      getRadius: 12, // Flag size
      getFillColor: d => {
        // Use the same color as the polygon
        const hex = d.color.replace('#', '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b, 255];
      },
      getLineColor: [255, 255, 255, 255], // White border
      getLineWidth: 2, // Border width
      radiusMinPixels: 12,
      radiusMaxPixels: 20,
      opacity: 1,
      stroked: true,
      pickable: false
    });
  }, [polygonLabels, zoomLevel]);

  // Return all team assignment layers
  const teamAssignmentLayers = useMemo(() => {
    const layers = [];
    if (polygonLayer) layers.push(polygonLayer);
    if (polygonLabelBgLayer) layers.push(polygonLabelBgLayer);
    if (polygonLabelsLayer) layers.push(polygonLabelsLayer);
    if (polygonCenterDotLayer) layers.push(polygonCenterDotLayer);
    
    
    
    return layers;
  }, [polygonLayer, polygonLabelBgLayer, polygonLabelsLayer, polygonCenterDotLayer]);

  return teamAssignmentLayers;
};

export default TeamAssignmentLayers; 